/**
 * DesktopDrawerStrategy
 *
 * Desktop strategy with 4 inline filter fields.
 * Shows: Location | Date | Guests | Adventure Type | Search button
 *
 * Phase 1: Basic 4-field inline layout (matching old SearchBar desktop layout)
 * Phase 2: Add "More filters" drawer with Price and Rating filters
 *
 * SOLID Compliance:
 * - SRP: Only renders desktop layout
 * - OCP: Extends FilterUIStrategy (can be replaced without modification)
 * - LSP: Fully substitutable for FilterUIStrategy
 * - ISP: Implements only required strategy interface
 * - DIP: Depends on abstractions (URLStateManager, FilterRepository)
 *
 * Layout:
 * ┌────────────────────────────────────────────────────────────┐
 * │ [Location] [Date] [Guests] [Activity Type] [🔍 Search]    │
 * └────────────────────────────────────────────────────────────┘
 *
 * Location: apps/web/src/components/search/strategies/DesktopDrawerStrategy.jsx
 * Created: 2025-10-31
 * Part of: MVP 1.9 - Adaptive Search & Filter System
 */

import React, { useState, useRef, useEffect } from 'react';
import { Search, SlidersHorizontal, X, ChevronDown } from 'lucide-react';
import FilterUIStrategy from './FilterUIStrategy';
import LocationAutocomplete from '../LocationAutocomplete';
import SearchDateRangePicker from '../../calendar/SearchDateRangePicker';
import GuestCountSelector from '../../calendar/GuestCountSelector';
import ActivityTypeSelector from '../ActivityTypeSelector';
import TagSelector from '../TagSelector';
import L2RefinementSection from '../L2RefinementSection';
import PriceRangeSlider from '../PriceRangeSlider';
import RatingSelector from '../RatingSelector';
import useSearchFilters from '../../../hooks/useSearchFilters';
import useTaxonomy from '../../../hooks/useTaxonomy';

class DesktopDrawerStrategy extends FilterUIStrategy {
  getType() {
    return 'desktop';
  }

  supportsAdvancedFilters() {
    return true; // Phase 2: Will show "More filters" drawer
  }

  getVisiblePrimaryFilterCount() {
    return 4; // All 4 primary filters visible inline
  }

  render(filters, urlStateManager, filterRepository) {
    return (
      <DesktopDrawerStrategyComponent
        filters={filters}
        urlStateManager={urlStateManager}
        filterRepository={filterRepository}
      />
    );
  }
}

/**
 * DesktopDrawerStrategyComponent - React Component
 */
function DesktopDrawerStrategyComponent({ filters, urlStateManager, filterRepository: _filterRepository }) {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showGuestPicker, setShowGuestPicker] = useState(false);
  const [showTypesDropdown, setShowTypesDropdown] = useState(false);
  const [showAdvancedDrawer, setShowAdvancedDrawer] = useState(false);
  const [drawerHeight, setDrawerHeight] = useState(0);
  const [searchBarHeight, setSearchBarHeight] = useState(0);
  const datePickerRef = useRef(null);
  const guestPickerRef = useRef(null);
  const dropdownRef = useRef(null);
  const drawerContentRef = useRef(null);
  const searchBarRef = useRef(null);
  const tabButtonRef = useRef(null);
  const drawerContainerRef = useRef(null);

  // Use centralized search filters hook
  const {
    locationInput,
    localStartDate,
    localEndDate,
    localGuestCount,
    localActivities,
    localTags,               // MVP 1.3 Phase 2
    handleLocationChange,
    handleLocationSelect,
    handleDateRangeSelect: hookHandleDateRangeSelect,
    handleGuestCountChange,
    handleActivityChange,
    handleTagToggle,         // MVP 1.3 Phase 2
    handleClearTags,         // MVP 1.3 Phase 2
    handleSearch: updateFilters,
    getDateDisplay,
    getGuestDisplay,
    getActivityDisplay,
  } = useSearchFilters(filters, urlStateManager);

  // Load taxonomy (MVP 1.3 Phase 2)
  const { taxonomy, loading: taxonomyLoading, error: _taxonomyError } = useTaxonomy();

  // Get advanced filter values from URL state (these update immediately via drawer)
  const minPrice = filters.minPrice;
  const maxPrice = filters.maxPrice;
  const minRating = filters.minRating || 0;

  // Close dropdowns and drawer when clicking outside
  useEffect(() => {
    const handleClickOutside = (_event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowTypesDropdown(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
      if (guestPickerRef.current && !guestPickerRef.current.contains(event.target)) {
        setShowGuestPicker(false);
      }
      if (drawerContainerRef.current && !drawerContainerRef.current.contains(event.target)) {
        setShowAdvancedDrawer(false);
      }
    };

    if (showTypesDropdown || showDatePicker || showGuestPicker || showAdvancedDrawer) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTypesDropdown, showDatePicker, showGuestPicker, showAdvancedDrawer]);

  // Close drawer with Escape key
  useEffect(() => {
    const handleEscapeKey = (_event) => {
      if (event.key === 'Escape' && showAdvancedDrawer) {
        setShowAdvancedDrawer(false);
      }
    };

    if (showAdvancedDrawer) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showAdvancedDrawer]);

  // Measure drawer and search bar heights dynamically
  useEffect(() => {
    if (showAdvancedDrawer && drawerContentRef.current && searchBarRef.current) {
      // Wait a tick for the drawer to start opening
      setTimeout(() => {
        if (drawerContentRef.current && searchBarRef.current) {
          const drawerH = drawerContentRef.current.offsetHeight;
          const searchBarH = searchBarRef.current.offsetHeight;
          setDrawerHeight(drawerH);
          setSearchBarHeight(searchBarH);
        }
      }, 50);
    }
  }, [showAdvancedDrawer]);

  // Remove focus from tab button when drawer opens or closes
  useEffect(() => {
    if (tabButtonRef.current) {
      tabButtonRef.current.blur();
    }
  }, [showAdvancedDrawer]);

  /**
   * Wrapper for date range selection - closes picker after selection
   */
  const handleDateRangeSelect = (start, end) => {
    hookHandleDateRangeSelect(start, end);
    setShowDatePicker(false);
  };

  /**
   * Execute search - updates URL which triggers search automatically
   */
  const handleSearch = () => {
    // Close adventure type dropdown if open
    setShowTypesDropdown(false);
    // Close advanced filters drawer if open
    setShowAdvancedDrawer(false);
    updateFilters(); // Updates URL via setSearchParams, triggers search in useEffect
  };

  /**
   * Handle price range change
   */
  const handlePriceChange = (min, max) => {
    const updates = {};
    if (min !== undefined && min > 0) {updates.minPrice = min;}
    if (max !== undefined && max < 1000) {updates.maxPrice = max;}

    // If both are default values, clear the filters
    if (min === 0 && max === 1000) {
      urlStateManager.clearFilter('minPrice');
      urlStateManager.clearFilter('maxPrice');
    } else {
      urlStateManager.updateFilters(updates);
    }
  };

  /**
   * Handle rating change
   */
  const handleRatingChange = (rating) => {
    if (rating === 0) {
      urlStateManager.clearFilter('minRating');
    } else {
      urlStateManager.updateFilter('minRating', rating);
    }
  };

  /**
   * Handle L2 category toggle (MVP 1.3 Phase 2 - Progressive Search)
   */
  const handleL2Toggle = (slug) => {
    const currentL2 = filters.categoryL2 || '';
    const l2Slugs = currentL2 ? currentL2.split(',').map(s => s.trim()) : [];

    // Toggle: remove if present, add if not present
    const isPresent = l2Slugs.includes(slug);
    const newL2Slugs = isPresent
      ? l2Slugs.filter(s => s !== slug)
      : [...l2Slugs, slug];

    // Update URL
    if (newL2Slugs.length > 0) {
      urlStateManager.updateFilter('categoryL2', newL2Slugs.join(','));
    } else {
      urlStateManager.clearFilter('categoryL2');
    }
  };

  /**
   * Count active advanced filters
   */
  const getAdvancedFiltersCount = () => {
    let count = 0;
    if (minPrice !== undefined || maxPrice !== undefined) {count++;}
    if (minRating > 0) {count++;}
    return count;
  };

  /**
   * Clear all advanced filters
   */
  const handleClearAdvancedFilters = () => {
    urlStateManager.clearFilter('minPrice');
    urlStateManager.clearFilter('maxPrice');
    urlStateManager.clearFilter('minRating');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Container for search bar + tab + drawer */}
      <div className="relative" ref={drawerContainerRef}>
        {/* 4 Inline Fields + Search Button */}
        <div ref={searchBarRef} className="relative z-30 flex items-center border border-gray-300 rounded-full shadow-sm hover:shadow-md transition bg-white">
          {/* Location Input with Autocomplete */}
          <div className="flex-1 px-6 py-2.5 border-r border-gray-300">
            <div className="text-xs font-semibold text-gray-900 mb-1">LOCATION</div>
            <LocationAutocomplete
              value={locationInput}
              onChange={handleLocationChange}
              onSelect={handleLocationSelect}
              placeholder="Anywhere"
              country="US"
              debugId="DesktopDrawerStrategy"
            />
          </div>

          {/* Date Input with Date Range Picker */}
          <div className="relative flex-1" ref={datePickerRef}>
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className={`w-full px-6 py-2.5 border-r border-gray-300 text-sm font-medium text-left hover:bg-gray-50 ${
                showDatePicker ? 'bg-gray-50' : ''
              }`}
            >
              <div className="text-xs font-semibold text-gray-900">DATE</div>
              <div className={`${localStartDate && localEndDate ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                {getDateDisplay()}
              </div>
            </button>
            {showDatePicker && (
              <div className="absolute top-full mt-2 left-0 z-50">
                <SearchDateRangePicker
                  onDateRangeSelect={handleDateRangeSelect}
                  onClose={() => setShowDatePicker(false)}
                />
              </div>
            )}
          </div>

          {/* Guest Count with Selector */}
          <div className="relative flex-1" ref={guestPickerRef}>
            <button
              onClick={() => setShowGuestPicker(!showGuestPicker)}
              className={`w-full px-6 py-2.5 border-r border-gray-300 text-sm font-medium text-left hover:bg-gray-50 ${
                showGuestPicker ? 'bg-gray-50' : ''
              }`}
            >
              <div className="text-xs font-semibold text-gray-900">GUESTS</div>
              <div className={`${localGuestCount > 1 ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                {getGuestDisplay()}
              </div>
            </button>
            {showGuestPicker && (
              <div className="absolute top-full mt-2 left-0 z-50">
                <GuestCountSelector
                  value={localGuestCount}
                  onChange={handleGuestCountChange}
                  onClose={() => setShowGuestPicker(false)}
                />
              </div>
            )}
          </div>

          {/* Adventure Type Filter + Search Button */}
          <div
            className={`flex-1 px-6 py-2.5 text-sm font-medium text-left hover:bg-gray-50 rounded-r-full flex items-center justify-between ${
              showTypesDropdown ? 'bg-gray-50' : ''
            }`}
          >
            <button
              onClick={() => setShowTypesDropdown(!showTypesDropdown)}
              className="flex-1 text-left"
            >
              <div className="text-xs font-semibold text-gray-900">ADVENTURE TYPE</div>
              <div className={`${localActivities.length > 0 ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                {getActivityDisplay()}
              </div>
            </button>
            <button
              onClick={handleSearch}
              className="p-2 bg-blue-600 rounded-full text-white ml-2 hover:bg-blue-700 transition flex-shrink-0"
              aria-label="Search"
            >
              <Search className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tab Trigger - Positioned to move with drawer, dynamically calculated */}
        <div
          className="absolute left-1/2 -translate-x-1/2 transition-all"
          style={{
            bottom: showAdvancedDrawer ? `${-drawerHeight + (searchBarHeight / 2)}px` : '0',
            transform: 'translateX(-50%) translateY(100%)',
            zIndex: 40,
            transitionProperty: 'bottom',
            transitionDuration: '400ms',
            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <button
            ref={tabButtonRef}
            onClick={() => setShowAdvancedDrawer(!showAdvancedDrawer)}
            aria-expanded={showAdvancedDrawer}
            aria-controls="advanced-filters-drawer"
            aria-label={`${showAdvancedDrawer ? 'Collapse' : 'Expand'} advanced filters`}
            className={`
              inline-flex items-center gap-2 px-6 py-1
              text-sm font-medium
              bg-gray-50 border-2 border-t-0 border-gray-300
              shadow-md
              hover:bg-gray-100 hover:shadow-lg
              transition-colors duration-200
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              ${showAdvancedDrawer
                ? 'text-blue-700 bg-blue-50 border-blue-300'
                : 'text-gray-700'
              }
            `}
            style={{
              minHeight: '32px',
              minWidth: '44px',
              borderBottomLeftRadius: '40px',
              borderBottomRightRadius: '40px'
            }}
          >
            {/* Filter Icon */}
            <SlidersHorizontal className="w-4 h-4" />

            {/* Text Label */}
            <span>More filters</span>

            {/* Chevron with rotation animation */}
            <div className={`transition-transform duration-300 ${
              showAdvancedDrawer ? 'rotate-180' : 'rotate-0'
            }`}>
              <ChevronDown className="w-4 h-4" />
            </div>

            {/* Count Badge */}
            {getAdvancedFiltersCount() > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-blue-600 rounded-full ml-1">
                {getAdvancedFiltersCount()}
              </span>
            )}
          </button>
        </div>

        {/* Drawer Container - Slides down from tab, starts at search bar midpoint */}
        <div
          id="advanced-filters-drawer"
          role="region"
          aria-label="Advanced search filters"
          className={`
            absolute top-1/2 left-0 right-0
            overflow-hidden rounded-b-2xl
            transition-all
            ${showAdvancedDrawer ? 'max-h-[700px] opacity-100' : 'max-h-0 opacity-0'}
          `}
          style={{
            zIndex: 25,
            transitionProperty: 'max-height, opacity',
            transitionDuration: '400ms',
            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <div ref={drawerContentRef} className="bg-white border-2 border-t-0 border-gray-300 shadow-2xl">
            {/* Drawer Header with Result Count */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 pt-[37px] pb-2 flex items-center justify-between z-10">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Advanced Filters
                </h3>
                {getAdvancedFiltersCount() > 0 && (
                  <p className="text-sm text-gray-600 mt-1">
                    {getAdvancedFiltersCount()} filter{getAdvancedFiltersCount() > 1 ? 's' : ''} applied
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowAdvancedDrawer(false)}
                aria-label="Close advanced filters"
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer Content - Advanced Filters */}
            <div className="p-6 space-y-4">
              {/* Price Range Filter */}
              <div className="border-b border-gray-200 pb-4">
                <PriceRangeSlider
                  minPrice={0}
                  maxPrice={1000}
                  value={{ min: minPrice || 0, max: maxPrice || 1000 }}
                  onChange={handlePriceChange}
                  currency="$"
                  step={10}
                />
              </div>

              {/* Rating Filter */}
              <div className="border-b border-gray-200 pb-4">
                <RatingSelector
                  value={minRating}
                  onChange={handleRatingChange}
                  maxRating={5}
                  showLabels={true}
                />
              </div>

              {/* Tag Filters (MVP 1.3 Phase 2) */}
              {!taxonomyLoading && taxonomy && taxonomy.tags && taxonomy.tags.length > 0 && (
                <div className="border-b border-gray-200 pb-4">
                  <TagSelector
                    tags={taxonomy.tags || []}
                    selectedTags={localTags}
                    onTagToggle={handleTagToggle}
                    onClear={handleClearTags}
                    groupByCategory={true}
                  />
                </div>
              )}

              {/* L2 Category Refinement (MVP 1.3 Phase 2 - Progressive Search) */}
              {!taxonomyLoading && taxonomy && filters.categoryL1 && (
                <div>
                  <L2RefinementSection
                    selectedL1Slugs={filters.categoryL1}
                    selectedL2Slugs={filters.categoryL2}
                    onL2Toggle={handleL2Toggle}
                  />
                </div>
              )}
            </div>

            {/* Drawer Footer with Actions */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <button
                onClick={handleClearAdvancedFilters}
                disabled={getAdvancedFiltersCount() === 0}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Clear all filters
              </button>

              <button
                onClick={() => setShowAdvancedDrawer(false)}
                className="px-6 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all shadow-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Adventure Types Dropdown (Outside tab/drawer container) */}
      {showTypesDropdown && (
        <div className="absolute top-full mt-2 right-0 z-50">
          <ActivityTypeSelector
            value={localActivities}
            onChange={handleActivityChange}
            variant="dropdown"
            onClose={() => setShowTypesDropdown(false)}
          />
        </div>
      )}
    </div>
  );
}

export default DesktopDrawerStrategy;
